import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, ShoppingCart, Edit, Trash2, CheckCircle2, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShoppingList() {
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '',
    weight: '',
    fiber_content: '',
    yardage: '',
    hook_size: '',
    notes: '',
    priority: 'medium',
    budget: ''
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const loadItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shopping-list`, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      logger.error('Failed to load shopping list:', error);
      toast.error('Failed to load shopping list');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shopping-list/marketplace-matches`, {
        withCredentials: true
      });
      setMatches(response.data);
    } catch (error) {
      logger.error('Failed to load matches:', error);
    }
  }, []);

  useEffect(() => {
    loadItems();
    loadMatches();
  }, [loadItems, loadMatches]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null
      };

      await axios.post(`${API}/shopping-list`, dataToSend, {
        withCredentials: true
      });

      toast.success('Item added to shopping list!');
      setShowAddDialog(false);
      setFormData({
        name: '',
        color: '',
        weight: '',
        fiber_content: '',
        yardage: '',
        hook_size: '',
        notes: '',
        priority: 'medium',
        budget: ''
      });
      loadItems();
      loadMatches();
    } catch (error) {
      logger.error('Failed to add item:', error);
      toast.error('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPurchased = async (itemId) => {
    try {
      await axios.put(`${API}/shopping-list/${itemId}`, 
        { purchased: true },
        { withCredentials: true }
      );
      toast.success('Marked as purchased!');
      loadItems();
    } catch (error) {
      logger.error('Failed to update item:', error);
      toast.error('Failed to update item');
    }
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    try {
      await axios.delete(`${API}/shopping-list/${deleteItemId}`, {
        withCredentials: true
      });
      toast.success('Item deleted');
      setDeleteItemId(null);
      loadItems();
      loadMatches();
    } catch (error) {
      logger.error('Failed to delete item:', error);
      toast.error('Failed to delete item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const activeItems = items.filter(item => !item.purchased);
  const purchasedItems = items.filter(item => item.purchased);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-2">
              Shopping List
            </h1>
            <p className="text-lg text-muted-foreground font-body">
              {activeItems.length} items to buy
            </p>
          </div>
          <Button
            data-testid="add-shopping-item-button"
            onClick={() => setShowAddDialog(true)}
            className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>

        {matches.length > 0 && (
          <div className="mb-8 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-6">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center">
              <ShoppingCart className="h-6 w-6 mr-2 text-accent" />
              Marketplace Matches
            </h2>
            <p className="text-sm text-muted-foreground mb-4">Found {matches.length} items matching your wishlist in the marketplace!</p>
            <Button
              onClick={() => navigate('/marketplace')}
              className="rounded-full bg-accent text-accent-foreground shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
            >
              Browse Marketplace
            </Button>
          </div>
        )}

        <div className="space-y-8">
          {activeItems.length === 0 && purchasedItems.length === 0 ? (
            <div className="text-center py-20 rounded-3xl bg-white border border-stone-100">
              <ShoppingCart className="h-24 w-24 text-muted-foreground/30 mx-auto mb-6" />
              <h3 className="text-2xl font-heading font-semibold mb-2">Your list is empty</h3>
              <p className="text-muted-foreground font-body mb-6">
                Add yarns you want to buy and we&apos;ll help you find them
              </p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </Button>
            </div>
          ) : (
            <>
              {activeItems.length > 0 && (
                <div>
                  <h2 className="text-2xl font-heading font-bold mb-4">To Buy</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeItems.map((item) => (
                      <div
                        key={item.item_id}
                        data-testid={`shopping-item-${item.item_id}`}
                        className="rounded-3xl bg-white border border-stone-100 p-6 hover:shadow-xl transition-all"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-xl font-heading font-semibold mb-1">
                              {item.name}
                            </h3>
                            {item.color && (
                              <p className="text-sm text-muted-foreground">{item.color}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            item.priority === 'high' ? 'bg-red-100 text-red-700' :
                            item.priority === 'low' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.priority}
                          </span>
                        </div>

                        <div className="space-y-2 mb-4 text-sm">
                          {item.weight && <p><span className="font-medium">Weight:</span> {item.weight}</p>}
                          {item.fiber_content && <p><span className="font-medium">Fiber:</span> {item.fiber_content}</p>}
                          {item.yardage && <p><span className="font-medium">Yardage:</span> {item.yardage}</p>}
                          {item.budget && (
                            <p className="flex items-center">
                              <DollarSign className="h-4 w-4 mr-1" />
                              <span className="font-medium">Budget:</span> ${item.budget}
                            </p>
                          )}
                          {item.notes && <p className="text-muted-foreground italic">{item.notes}</p>}
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={() => handleMarkPurchased(item.item_id)}
                            className="flex-1 rounded-full bg-accent text-accent-foreground"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Purchased
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteItemId(item.item_id)}
                            className="rounded-full hover:bg-red-50 text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {purchasedItems.length > 0 && (
                <div>
                  <h2 className="text-2xl font-heading font-bold mb-4 text-muted-foreground">Purchased</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-60">
                    {purchasedItems.map((item) => (
                      <div
                        key={item.item_id}
                        className="rounded-3xl bg-stone-50 border border-stone-100 p-6"
                      >
                        <h3 className="text-xl font-heading font-semibold mb-1 line-through">
                          {item.name}
                        </h3>
                        {item.color && <p className="text-sm text-muted-foreground">{item.color}</p>}
                        <div className="mt-4 flex items-center text-accent">
                          <CheckCircle2 className="h-5 w-5 mr-2" />
                          <span className="font-medium">Purchased</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Add to Shopping List</DialogTitle>
              <DialogDescription>
                Add a yarn you want to buy and we&apos;ll help you find it
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div>
                <Label htmlFor="name">Yarn Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="color">Color</Label>
                  <Input
                    id="color"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="weight">Weight</Label>
                  <Input
                    id="weight"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="budget">Budget ($)</Label>
                <Input
                  id="budget"
                  name="budget"
                  type="number"
                  step="0.01"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border-stone-200 bg-stone-50 px-3 py-2"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="rounded-xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-full bg-primary text-primary-foreground"
              >
                {saving ? 'Adding...' : 'Add Item'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this item from your shopping list?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
